import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { Loader2, Check, Mail, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY_CONTACT = { required_buyers: "", required_suppliers: "", required_technical_reviewers: "", required_approvers: "", additional_requirements: "" };

export default function EnterpriseSubscription() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [requesting, setRequesting] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/subscriptions/plans").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/enterprise/overview").then((r) => setOverview(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const requestPlan = async (planId) => {
    setRequesting(planId);
    try {
      const r = await api.post("/subscriptions/request", { plan_id: planId });
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRequesting("");
    }
  };

  const mailtoHref = () => {
    const to = data?.sales_email || "";
    const subject = "Custom ProcDeal Enterprise Plan Request";
    const body = [
      "Custom ProcDeal Enterprise Plan Request",
      "",
      `Enterprise Name: ${overview?.enterprise?.name || ""}`,
      `Enterprise Admin Name: ${user?.name || ""}`,
      `Enterprise Email: ${user?.email || ""}`,
      `Current Plan: ${data?.current?.plan?.name || "None"}`,
      "Required Buyer Count: ",
      "Required Supplier Count: ",
      "Required Technical Reviewer Count: ",
      "Required Approver Count: ",
      "Additional Requirements: ",
    ].join("\n");
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const sendContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = Object.fromEntries(Object.entries(contact).map(([k, v]) => [k, k === "additional_requirements" ? v : Number(v) || 0]));
      const r = await api.post("/contact/custom-plan", payload);
      toast.success(r.data.message);
      setContactOpen(false);
      setContact(EMPTY_CONTACT);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const { plans, current, pending_request } = data;

  return (
    <div className="space-y-6" data-testid="enterprise-subscription-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Subscription & Pricing</h1>
        <p className="mt-1 text-sm text-muted-foreground">Choose the plan that fits your procurement team. Plans activate after payment confirmation.</p>
      </div>

      {current && (
        <div data-testid="current-plan-banner" className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
          <p className="text-sm text-green-900">
            Active plan: <span className="font-semibold">{current.plan?.name}</span>
            {current.period_end && <> · valid until {new Date(current.period_end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</>}
          </p>
        </div>
      )}
      {pending_request && (
        <div data-testid="pending-request-banner" className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <Clock className="h-5 w-5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-900">
            Your request for the <span className="font-semibold">{pending_request.plan?.name || pending_request.plan_id}</span> plan is <span className="font-semibold">pending activation</span>. Limits unlock once payment is confirmed.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {plans.map((p) => {
          const isCurrent = current?.plan_id === p.id;
          const isPending = pending_request?.plan_id === p.id;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`flex flex-col rounded-xl border bg-white p-6 ${isCurrent ? "border-green-400 ring-1 ring-green-300" : ""}`}>
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold">{p.name}</h2>
                {isCurrent && <span data-testid={`plan-current-badge-${p.id}`} className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">Current</span>}
                {isPending && <span data-testid={`plan-pending-badge-${p.id}`} className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">Pending activation</span>}
              </div>
              <p className="mt-2 font-heading text-3xl font-semibold" data-testid={`plan-price-${p.id}`}>
                {p.price_inr != null ? <>{fmtMoney(p.price_inr)}<span className="text-sm font-normal text-muted-foreground">/year</span></> : "Custom pricing"}
              </p>
              <ul className="mt-4 flex-1 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-slate-900" />{f}
                  </li>
                ))}
              </ul>
              {p.id === "custom" ? (
                <div className="mt-6 space-y-2">
                  <a href={mailtoHref()} data-testid="custom-contact-mailto"
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800">
                    <Mail className="h-4 w-4" /> Contact Us
                  </a>
                  <button type="button" data-testid="custom-contact-form-link" onClick={() => setContactOpen(true)}
                    className="w-full text-center text-xs text-muted-foreground hover:text-slate-900 hover:underline">
                    Can't open your email client? Use the contact form
                  </button>
                </div>
              ) : (
                <Button data-testid={`plan-request-${p.id}`} disabled={!!pending_request || requesting === p.id}
                  variant={isCurrent ? "outline" : "default"}
                  className={isCurrent ? "mt-6" : "mt-6 bg-slate-900 hover:bg-slate-800"}
                  onClick={() => requestPlan(p.id)}>
                  {requesting === p.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isCurrent ? "Request renewal" : isPending ? "Requested" : "Choose plan"}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="custom-contact-dialog">
          <DialogHeader><DialogTitle>Custom Plan Request</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Tell us your requirements and our sales team will reach out. Sent to {data.sales_email}.</p>
          <form onSubmit={sendContact} className="mt-2 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cc-buyers">Required Buyers</Label>
                <Input id="cc-buyers" data-testid="contact-required-buyers" type="number" min="0" value={contact.required_buyers}
                  onChange={(e) => setContact((c) => ({ ...c, required_buyers: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cc-suppliers">Required Suppliers</Label>
                <Input id="cc-suppliers" data-testid="contact-required-suppliers" type="number" min="0" value={contact.required_suppliers}
                  onChange={(e) => setContact((c) => ({ ...c, required_suppliers: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cc-trs">Technical Reviewers</Label>
                <Input id="cc-trs" data-testid="contact-required-reviewers" type="number" min="0" value={contact.required_technical_reviewers}
                  onChange={(e) => setContact((c) => ({ ...c, required_technical_reviewers: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cc-appr">Approvers</Label>
                <Input id="cc-appr" data-testid="contact-required-approvers" type="number" min="0" value={contact.required_approvers}
                  onChange={(e) => setContact((c) => ({ ...c, required_approvers: e.target.value }))} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="cc-notes">Additional Requirements</Label>
              <textarea id="cc-notes" data-testid="contact-additional" rows={3} value={contact.additional_requirements}
                onChange={(e) => setContact((c) => ({ ...c, additional_requirements: e.target.value }))}
                className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
            </div>
            <Button type="submit" data-testid="contact-submit" disabled={sending} className="w-full bg-slate-900 hover:bg-slate-800">
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send request
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
